import { Token } from 'config/types'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import { useState } from 'react'
import { Trans } from 'react-i18next'
import { useActiveBonusInWageringProcess } from 'state/bonus/hooks'
import { Icons } from 'svgs'
import theme from 'theme'
import { ModalBody, ModalHeader } from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import { ModalProps } from 'UIKit/ModalV2'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import HunnyTooltip from 'UIKit/Tooltip'
import { emptyBonus } from 'views/Payment/components/Deposit/DepositCryptos/hooks'
import { PAYMENT_MODAL_THEME, PaymentStroke, StyledActionButton, StylePaymentModal } from '../styles'
import WaitingDepositBonusItem from './WaitingDepositBonusItem'

const WarningTooltip = () => {
  return (
    <HunnyTooltip
      id="ctp-deposit-bonus"
      place="top"
      offset={6}
      style={{
        padding: '6px 12px',
        borderRadius: 'var(--pm-trigger-radius-small)',
        width: '320px',
      }}
    >
      <TransText color="var(--pm-label-color)" fontSize="12px" fontWeight={300}>
        To activate this bonus, you must either complete the current wagering bonus or cancel it.
      </TransText>
    </HunnyTooltip>
  )
}

const WarningBonus: React.FC<{
  isExpired: boolean
}> = ({ isExpired }) => {
  return (
    <RowMiddle
      p="12px"
      borderRadius="4px"
      gap="4px"
      style={{
        backgroundColor: 'rgba(255, 208, 38, 0.10',
      }}
    >
      <Box minWidth={16}>
        <Icons.WarningIcon width="16px" height="16px" fill={theme.colors.warning} />
      </Box>

      <Text fontSize="12px" fontWeight={400}>
        {isExpired ? (
          <Trans>Your bonus has been ended. Please choose another bonus</Trans>
        ) : (
          <Trans>Please don’t change the selected bonus until your deposit is credited</Trans>
        )}
      </Text>
    </RowMiddle>
  )
}

const WaitingDepositBonusModal: React.FC<
  ModalProps<{
    selectedToken: Token
    disabled?: boolean
    bonusControl: {
      bonuses?: WageringConditionBonus[]
      selectedBonus?: WageringConditionBonus
      handleSelectedBonus?: (bonus: WageringConditionBonus) => void
      isSelectedBonusExpired?: boolean
    }
  }>
> = ({ onDismiss, data }) => {
  const { bonusControl, disabled, selectedToken } = data
  const { bonuses, selectedBonus: selectedBonusProps, handleSelectedBonus, isSelectedBonusExpired } = bonusControl

  const [selectedBonus, setBonus] = useState(selectedBonusProps)
  const bonusWagering = useActiveBonusInWageringProcess()

  const onSelectBonus = () => {
    handleSelectedBonus(selectedBonus || emptyBonus)
    onDismiss()
  }

  return (
    <StylePaymentModal
      minWidth={['100%', '', '', '400px !important']}
      maxWidth={['100%', '', '', '400px !important']}
      colorTheme={PAYMENT_MODAL_THEME}
      onDismiss={onDismiss}
    >
      <ModalHeader colorTheme={PAYMENT_MODAL_THEME} title="Choose Bonus" />
      <ModalBody gap="20px">
        <WarningBonus isExpired={isSelectedBonusExpired} />

        <Column alignItems="flex-start" minHeight={150} maxHeight={300} overflowY="scroll" overflowX="hidden">
          {bonuses.map((bonus, index) => {
            return (
              <>
                <WaitingDepositBonusItem
                  disabled={disabled || (bonusWagering && bonus?.isCreaditReward)}
                  key={bonus.id}
                  value={bonus}
                  bonus={bonus}
                  selectedToken={selectedToken}
                  isSelected={bonus.id === selectedBonus?.id}
                  data-tooltip-id={
                    bonusWagering && bonus?.isCreaditReward && bonus.id !== emptyBonus.id ? 'ctp-deposit-bonus' : null
                  }
                  onSelectBonus={() => setBonus(bonus === selectedBonus ? null : bonus)}
                  onExpired={() => {
                    setBonus(null)
                  }}
                />
                {index < bonuses.length - 1 && <PaymentStroke marginY="8px" />}
              </>
            )
          })}
        </Column>
        {selectedBonus && selectedBonus?.id !== 0 ? (
          <StyledActionButton disabled={!selectedBonus || !bonuses.length} onClick={onSelectBonus}>
            <Text small bold>
              <Trans>Apply</Trans>
            </Text>
          </StyledActionButton>
        ) : (
          <StyledActionButton
            onClick={onSelectBonus}
            style={{
              background: '#FFE3F3',
            }}
          >
            <Text small bold color="primary">
              <Trans>Continue Without Bonus</Trans>
            </Text>
          </StyledActionButton>
        )}

        <WarningTooltip />
      </ModalBody>
    </StylePaymentModal>
  )
}

export default WaitingDepositBonusModal
